/* eslint-disable @typescript-eslint/no-empty-function */
import {
  createContext,
  useMemo,
  useState,
  useContext,
  useCallback,
  useEffect,
} from "react";
import { ListField } from "../types/status";
import { getStatusValues } from "../status";
import { Product } from "../types/product";
import { getAllProducts } from "../product";
import localStorageAvailable from "../../../utils/localStorageAvailable";
import { CustomFields } from "../types/userFields";
import { getCustomListFields } from "../userFields";
import {
  CACHE_KEY,
  LEGACY_CACHE_KEYS,
  LIST_KEY,
  STATUS_ENTITY_ID,
} from "../accountConfig";

type BitrixContextType = {
  listFields: ListField[];
  productsList: Product[];
  userfieldList: CustomFields[];
};

export const BitrixContext = createContext<BitrixContextType | null>(null);

// ----------------------------------------------------------------------

type BitrixProviderProps = {
  children: React.ReactNode;
};

export function BitrixProvider({ children }: BitrixProviderProps) {
  const [listFields, setListFields] = useState<ListField[]>([]);
  const [productsList, setProductsList] = useState<Product[]>([]);
  const [userfieldList, setUserFieldList] = useState<CustomFields[]>([]);

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    if (storageAvailable) {
      // Las listas cacheadas de la cuenta anterior no sirven: los IDs de campo,
      // de valor de lista y de embudo son distintos en cada cuenta de Bitrix.
      LEGACY_CACHE_KEYS.forEach((key) => localStorage.removeItem(key));

      // listFields =========================
      const storedListFields = localStorage.getItem(CACHE_KEY.listFields);
      const storedListFieldsObject =
        storedListFields && JSON.parse(storedListFields);

      if (storedListFieldsObject) {
        setListFields(storedListFieldsObject);
      } else {
        const newListFields = [
          {
            FIELD_NAME: LIST_KEY.DEAL_STAGE,
            LIST: await getStatusValues(STATUS_ENTITY_ID.DEAL_STAGE),
          },
          {
            FIELD_NAME: LIST_KEY.DEAL_TYPE,
            LIST: await getStatusValues(STATUS_ENTITY_ID.DEAL_TYPE),
          },
          {
            FIELD_NAME: LIST_KEY.SMART_INVOICE_STAGE,
            LIST: await getStatusValues(STATUS_ENTITY_ID.SMART_INVOICE_STAGE),
          },
          {
            FIELD_NAME: LIST_KEY.SOURCE,
            LIST: await getStatusValues(STATUS_ENTITY_ID.SOURCE),
          },
          {
            FIELD_NAME: LIST_KEY.INDUSTRY,
            LIST: await getStatusValues(STATUS_ENTITY_ID.INDUSTRY),
          },
        ];

        setListFields(newListFields);
        localStorage.setItem(
          CACHE_KEY.listFields,
          JSON.stringify(newListFields)
        );
      }

      // productsList =========================
      const storedProductsList = localStorage.getItem(CACHE_KEY.productsList);
      const storedProductsListObject =
        storedProductsList && JSON.parse(storedProductsList);

      // if (storedProductsListObject) {
      //   setProductsList(storedProductsListObject);
      // } else {
      const newProductsList = await getAllProducts();

      setProductsList(newProductsList);
      localStorage.setItem(
        CACHE_KEY.productsList,
        JSON.stringify(newProductsList)
      );
      // }

      // userfieldList =========================
      const storedUserfieldList = localStorage.getItem(CACHE_KEY.userfieldList);
      const storedUserfieldListObject =
        storedUserfieldList && JSON.parse(storedUserfieldList);

      if (storedUserfieldListObject) {
        setUserFieldList(storedUserfieldListObject);
      } else {
        const newUserfieldList = [
          // ...(await getCustomListFields("lead")),
          // ...(await getCustomListFields("deal")),
          ...(await getCustomListFields("contact")),
          ...(await getCustomListFields("company")),
        ];

        setUserFieldList(newUserfieldList);
        localStorage.setItem(
          CACHE_KEY.userfieldList,
          JSON.stringify(newUserfieldList)
        );
      }
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const memoizedValue = useMemo(
    () => ({
      listFields,
      productsList,
      userfieldList,
    }),
    [listFields, productsList, userfieldList]
  );

  return (
    <BitrixContext.Provider value={memoizedValue}>
      {children}
    </BitrixContext.Provider>
  );
}

export const useBitrixContext = () => {
  const context = useContext(BitrixContext);

  if (!context)
    throw new Error(
      "useBitrixContext context must be use inside BitrixProvider"
    );

  return context;
};
